// ===========================================
// Legal Settings Tab
// Admin Settings - Legal (Terms of Service, Privacy Policy)
// ===========================================

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Stack,
  Alert,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import {
  useLegalSettings,
  useUpdateLegalSettings,
  useResetLegalSettings,
} from '../../../hooks/useAdminSettings';
import SettingsWriteGate from './SettingsWriteGate';

// ===========================================
// TYPES
// ===========================================

interface LegalFormData {
  termsOfService: string;
  privacyPolicy: string;
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function LegalSettingsTab() {
  const { data: settings, isLoading, error } = useLegalSettings();
  const updateMutation = useUpdateLegalSettings();
  const resetMutation = useResetLegalSettings();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<LegalFormData>({
    defaultValues: {
      termsOfService: '',
      privacyPolicy: '',
    },
  });

  // Update form when data loads
  useEffect(() => {
    if (settings) {
      reset({
        termsOfService: settings.termsOfService || '',
        privacyPolicy: settings.privacyPolicy || '',
      });
    }
  }, [settings, reset]);

  const onSubmit = (data: LegalFormData) => {
    updateMutation.mutate({
      termsOfService: data.termsOfService || null,
      privacyPolicy: data.privacyPolicy || null,
    });
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={300} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load legal settings. Please try again.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Terms of Service */}
        <Card variant="outlined">
          <CardHeader
            title="Terms of Service"
            subheader="Paste your school's terms of service here"
            avatar={<GavelIcon color="primary" />}
          />
          <CardContent>
            <Controller
              name="termsOfService"
              control={control}
              rules={{
                maxLength: {
                  value: 100000,
                  message: 'Terms of Service must be 100,000 characters or less',
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  multiline
                  minRows={8}
                  maxRows={20}
                  fullWidth
                  placeholder="Paste your Terms of Service here..."
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    `${field.value?.length || 0} characters`
                  }
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Privacy Policy */}
        <Card variant="outlined">
          <CardHeader
            title="Privacy Policy"
            subheader="Paste your school's privacy policy here"
            avatar={<PolicyIcon color="primary" />}
          />
          <CardContent>
            <Controller
              name="privacyPolicy"
              control={control}
              rules={{
                maxLength: {
                  value: 100000,
                  message: 'Privacy Policy must be 100,000 characters or less',
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  multiline
                  minRows={8}
                  maxRows={20}
                  fullWidth
                  placeholder="Paste your Privacy Policy here..."
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    `${field.value?.length || 0} characters`
                  }
                />
              )}
            />
          </CardContent>
        </Card>

        <Alert severity="info" icon={false}>
          <Typography variant="body2">
            These documents will be available to users during registration and from their account settings.
          </Typography>
        </Alert>

        {/* Action Buttons */}
        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending || updateMutation.isPending}
            >
              {resetMutation.isPending ? 'Clearing...' : 'Clear All'}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
